import { Body, BodyBold, BodyGrey } from '../../Typography';
import { Box, MenuItem, Select, styled } from '@mui/material';
import { fontWeight } from '../../../theme/typography';
import { HFlex, VFlex } from '../../FlexContainers';
import { palette } from '../../../theme/palette';
import { RootContainer } from '../../layout/Containers';
import { PrimaryButton } from '../../Buttons';

export const Root = styled(RootContainer)`
  background-color: ${palette.custom.lightGrey};
  min-height: 100vh;
  width: 100%;
  gap: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 45px;
  padding-bottom: 45px;
`;

export const Card = styled(VFlex, {
  shouldForwardProp: (prop) => prop !== 'hasExtraPadding',
})<{
  hasExtraPadding?: boolean;
}>`
  background: white;
  padding: 30px 45px;
  justify-content: space-between;
  width: 100%;
  max-width: 434px;
  border-radius: 5px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.down('xs')} {
    padding: 22px;
  }

  &.get-started-signup-flow-card {
    padding: 25px 61px 33px 61px;
    max-width: 453px;

    .signup-flow-card-header {
      margin-bottom: 25px;
    }

    .signup-flow-card-footer {
      margin-top: 20px;
    }

    .signup-flow-footer-separator {
      margin-top: 20px;
    }

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 32px 24px 33px 23px;

      .signup-flow-card-footer {
        margin-top: 14px;
      }
    }
  }

  &.get-started-signup-flow-card.is-resend {
    padding: 30px 61px 34px 61px;

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 32px 24px 33px 23px;
    }
  }

  &.link-expired-signup-flow-card {
    padding: 30px 63px 33px 63px;

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 32px 24px 33px 23px;
    }
  }

  &.verify-email-signup-flow-card {
    padding: 34px 70px 30px 70px;

    .signup-flow-card-header-title {
      letter-spacing: 0.8px;
    }

    .signup-flow-footer-separator {
      margin-top: 30px;
    }

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 34px 23px 30px 23px;

      .signup-flow-card-footer {
        margin-top: 14px;
      }
    }
  }

  &.confirm-account-signup-flow-card {
    padding: 30px 69px 32px 69px;

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 25px 27px 20px 27px;

      .signup-flow-card-header {
        margin-bottom: 30px;
      }

      .signup-flow-card-footer {
        margin-top: 10px;
      }
    }
  }

  &.signup-flow-account-form-card {
    padding: 34px 53px 30px 52px;

    .signup-flow-card-header-title {
      letter-spacing: 0.8px;
    }

    .signup-flow-card-company-header-title {
      line-height: 36px;
      letter-spacing: 0.8px;
    }

    .create-account-form {
      gap: 12px;
    }

    .create-account-form-phone-field {
      margin-bottom: 6px;
    }

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 24px 25px 23px 25px;

      .signup-flow-card-header {
        margin-bottom: 25px;
      }
    }
  }

  &.invite-team-signup-flow-card {
    padding: 40px 53px 30px 52px;

    .signup-flow-card-header-title {
      line-height: 36px;
      letter-spacing: 0.8px;
    }

    .signup-flow-card-footer {
      margin-top: 22px;
    }

    ${(props) => props.theme.breakpoints.down('xs')} {
      padding: 24px 24px 24px 23px;

      .signup-flow-card-header {
        margin-bottom: 20px;
      }

      .signup-flow-card-footer {
        margin-top: 14px;
      }
    }
  }
`;

export const CardHeader = styled(VFlex)`
  align-items: center;
  gap: 4px;
  margin-bottom: 35px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    gap: 0px;
  }
`;

export const CardBody = styled(Box)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const CardTitle = styled(Body, {
  shouldForwardProp: (prop) => prop !== 'largerFont',
})<{
  largerFont?: boolean;
}>`
  text-align: center;
  font-size: ${(props) => (props.largerFont ? '38px' : '32px')};
  font-weight: ${fontWeight.heavy};
  line-height: 38px;
  letter-spacing: 0.95px;
  text-transform: capitalize;

  ${(props) => props.theme.breakpoints.down('xs')} {
    font-size: 24px;
    line-height: 30px;
    letter-spacing: unset;
  }
`;

export const CardSubtitle = styled(BodyGrey)`
  margin-bottom: 0px;
`;

export const Description = styled(Body)`
  line-height: 22px;
  margin-bottom: 0px;
  span {
    margin: 0 5px;
  }
`;

export const Link = styled(Body)`
  color: ${palette.collection.blue.d400};
  cursor: pointer;
  margin-bottom: 0px;
  text-decoration: none;
`;

export const SkipLink = styled(Body)`
  color: ${palette.collection.grey.d750};
  cursor: pointer;
  margin-bottom: 0px;
  text-decoration: underline;
  font-weight: ${fontWeight.bold};
  margin: 0 5px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    font-weight: ${fontWeight.regular};
  }
`;

export const ContinueButton = styled(PrimaryButton)`
  height: 40px;
  margin-top: 30px;
`;

export const Separator = styled('hr', {
  shouldForwardProp: (prop) => prop !== 'marginTop',
})<{
  marginTop?: number;
}>`
  border: none;
  border-top: 1px solid ${palette.collection.grey.d250};
  width: 100%;
  margin-top: ${(props) => (props.marginTop ? props.marginTop + 'px' : '4px')};
  margin-bottom: 4px;
`;

export const Email = styled(BodyBold)`
  word-break: break-all;
`;

export const CardFooter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'leftAlign',
})<{ center?: boolean }>`
  margin-top: 14px;

  ${(props) =>
    props.center &&
    `
      display: flex;
      justify-content: center;
  `};
`;

export const FooterText = styled(Body, {
  shouldForwardProp: (prop) => prop !== 'nonBold',
})<{ nonBold?: boolean }>`
  span {
    margin: 0 5px;
  }

  ${(props) => props.theme.breakpoints.down('xs')} {
    line-height: 20px;
  }

  font-weight: ${(props) => (props.nonBold ? 'normal' : 'bold')};
`;

export const SignupCardFooter = styled(VFlex)`
  margin-top: 16px;
  align-items: center;
  gap: 16px;
`;

export const InviteUsersList = styled(VFlex)`
  gap: 15px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 4px 0px 2px 0px;
    gap: 7px;
  }
`;

export const InviteUserItem = styled(VFlex)``;

export const InviteUserDetails = styled(HFlex)`
  align-items: center;
  justify-content: space-between;
  gap: 15px;
`;

export const InviteUserEmailAndRoleItem = styled(VFlex)`
  font-size: 14px;
`;

export const InviteUserEmail = styled(BodyGrey)`
  line-height: 20px;
  margin-bottom: 0px;
  word-break: break-all;
`;

export const InviteUserResponseDetails = styled(HFlex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
`;

export const InviteUserResponseEmail = styled(BodyGrey)`
  line-height: 20px;
  margin-bottom: 0px;
  word-break: break-all;
  font-size: 14px;
`;

export const InviteUserResponseRole = styled(BodyGrey)`
  line-height: 20px;
  margin-bottom: 0px;
  word-break: break-all;
  font-size: 14px;
`;

export const InviteUserResposeStatus = styled(VFlex)`
  align-items: end;
  width: 95px;
  padding-top: 3px;
`;

export const InviteUserSuccessResposeStatus = styled(BodyGrey)`
  margin-bottom: 0px;
  word-break: break-all;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0.3px;
  color: ${palette.custom.mediumGreen};
`;

export const InviteUserFailureResposeStatus = styled(BodyGrey)`
  margin-bottom: 0px;
  word-break: break-all;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0.3px;
  color: ${palette.error.main};
`;

export const InvitationStatusHeader = styled(HFlex)`
  font-size: 12px;
  display: flex;
`;

export const InvitationStatusHeaderText = styled(BodyGrey)`
  display: flex;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.3px;
`;

export const SuccessCount = styled(BodyGrey)`
  color: ${palette.custom.mediumGreen};
  font-size: 12px;
  margin-top: 1px;
  margin-right: 4px;
  line-height: 14px;
  letter-spacing: 0.3px;
  font-weight: 700;
`;

export const FailureCount = styled(BodyGrey)`
  color: ${palette.error.main};
  font-size: 12px;
  margin-top: 1px;
  margin-right: 4px;
  padding-left: 16px;
  line-height: 14px;
  letter-spacing: 0.3px;
  font-weight: 700;
`;

export const InviteUserRole = styled(Select)`
  height: 30px;
  margin: 0px;
  text-transform: capitalize;
  font-size: 14px;
  .MuiOutlinedInput-notchedOutline {
    border: 0px;
  }
  .MuiSelect-icon {
    right: 0;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    font-size: 12px;
    color: ${palette.collection.grey.d750};
  }
`;

export const Option = styled(MenuItem)`
  font-size: 14px;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  align-items: baseline;
  flex-wrap: wrap;
  :hover,
  &.Mui-selected:hover {
    background-color: ${palette.custom.lightGrey} !important;
  }
  &.Mui-selected {
    background-color: ${palette.custom.grey100} !important;
  }
  &.MuiMenuItem-root {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

export const OptionLabel = styled(Body)`
  font-size: 14px;
  text-transform: capitalize;
  font-weight: ${fontWeight.bold};
`;

export const OptionDescription = styled(Body)`
  font-size: 12px;
  white-space: pre-wrap;
`;
